import { useState, useMemo, useCallback, useRef } from 'react'
import type React from 'react'

import { faAngleUp, faAngleDown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { UnstyledButton } from '@b-stock/fe-core/bstock-next/components'
import { Colors } from '@b-stock/fe-core/design-system'

import StyledScroll from '@/components/common/modal/scrollbarStyles'

import { useTrackFilterUsage } from './analytics'
import OnlyableCheckbox from './OnlyableCheckbox'
import type { InternalNode } from './regionFilterUtils'
import {
  hasEveryChild,
  getChildrenKeys,
  getChildKeys,
  getCountryKey,
  getGrandchildrenKeys,
} from './regionFilterUtils'

type InternalNodeState = 'FULL' | 'EMPTY' | 'PARTIAL'
type InternalNodeStateMap = Partial<Record<string, InternalNodeState>>

export type KeySet = Set<string>

type Props = {
  label?: string
  allNodes: InternalNode[]
  displayNodes: InternalNode[]
  selected: KeySet
  setSelected: (value: KeySet) => void
  shouldNodeBeExpanded?: (node: InternalNode) => boolean
}

type TrackFilterClick = (args: {
  device: 'Mobile' | 'Desktop'
  filter_name: string
  is_selected: boolean
}) => void

type InternalNodeComponentProps = {
  label?: string
  node: InternalNode
  state: InternalNodeState
  toggleSelected: (key: string, setOnly?: boolean) => void
  toggleInternal: (key: string, setOnly?: boolean) => void
  selected: KeySet
  initialECState?: boolean
  nodeStates: InternalNodeStateMap
  trackFilterClick: TrackFilterClick
  onNodeClick?: (e: React.MouseEvent<HTMLElement>, key: string) => void
  suppressNextChangeRef?: React.RefObject<boolean>
}

const NodeList = styled.ol`
  list-style: none;
  display: flex;
  flex-direction: column;
  position: relative;
  row-gap: 0.75rem;
  ${StyledScroll}
`

const LeafNodeList = styled.ol`
  list-style: none;
  padding: 0 0 0 0.5rem;
  margin: 0.75rem 0 0;
  display: flex;
  flex-direction: column;
  row-gap: 0.75rem;
  width: 100%;
  overflow: hidden;

  &:not(:has(li > div > button)) {
    padding-left: 1.75rem;
  }
`

const InternalHeading = styled.div`
  display: flex;
  width: 100%;
`

const ECButton = styled(UnstyledButton)`
  width: 1rem;
  height: 1rem;
  align-items: center;
  justify-content: center;
  margin-right: 0.375rem;
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const InternalNodeComponent = ({
  label,
  node,
  state,
  selected,
  toggleSelected,
  toggleInternal,
  initialECState,
  nodeStates,
  trackFilterClick,
  onNodeClick,
  suppressNextChangeRef,
}: InternalNodeComponentProps) => {
  const [isExpanded, setIsExpanded] = useState(initialECState)
  const toggleExpanded = useCallback(() => setIsExpanded((v) => !v), [])

  const handleFilterClick = (
    name: string,
    checked: boolean | 'PARTIAL' | 'FULL' | 'EMPTY'
  ) => {
    trackFilterClick({
      device: 'Desktop',
      filter_name: name,
      is_selected:
        checked === 'PARTIAL' || checked === 'EMPTY' || checked === false, // User is clicking on an unselected checkbox to flip the state to selected
    })
  }
  const { formatMessage } = useIntl()

  return (
    <li>
      <InternalHeading>
        {node.children.length > 0 ? (
          <>
            <ECButton
              onClick={toggleExpanded}
              type="button"
              aria-label={formatMessage(
                {
                  id: `Filters.hierarchical.${isExpanded ? 'collapse' : 'expand'}`,
                },
                { displayName: node.displayName }
              )}
            >
              <FontAwesomeIcon icon={isExpanded ? faAngleUp : faAngleDown} />
            </ECButton>

            <OnlyableCheckbox
              as="div"
              displayName={node.displayName}
              checked={state === 'PARTIAL' ? 'PARTIAL' : state === 'FULL'}
              onChange={() => {
                if (suppressNextChangeRef?.current) {
                  suppressNextChangeRef.current = false
                  return
                }
                toggleInternal(node.key)
              }}
              onOnly={() => toggleInternal(node.key, true)}
              onFilterClick={() => handleFilterClick(node.displayName, state)}
              onClick={(e) => onNodeClick?.(e, node.key)}
            />
          </>
        ) : (
          <OnlyableCheckbox
            as="div"
            displayName={node.displayName}
            checked={selected.has(node.key)}
            onChange={() => {
              if (suppressNextChangeRef?.current) {
                suppressNextChangeRef.current = false
                return
              }
              toggleSelected(node.key)
            }}
            onOnly={() => toggleSelected(node.key, true)}
            onFilterClick={() =>
              handleFilterClick(node.displayName, selected.has(node.key))
            }
            onClick={(e) => onNodeClick?.(e, node.key)}
          />
        )}
      </InternalHeading>

      {isExpanded && node.children.length > 0 && (
        <LeafNodeList>
          {node.children.map((lNode) => (
            <InternalNodeComponent
              key={lNode.key}
              label={label}
              state={nodeStates[lNode.key] || state}
              node={lNode}
              nodeStates={nodeStates}
              selected={selected}
              toggleSelected={toggleSelected}
              toggleInternal={toggleInternal}
              initialECState={initialECState}
              trackFilterClick={trackFilterClick}
              onNodeClick={onNodeClick}
              suppressNextChangeRef={suppressNextChangeRef}
            />
          ))}
        </LeafNodeList>
      )}
    </li>
  )
}

// Helper function to find a node by its key in the tree
const findNodeByKey = (
  nodeList: InternalNode[],
  key: string
): InternalNode | null => {
  for (const node of nodeList) {
    if (node.key === key) return node
    const childResult = findNodeByKey(node.children, key)
    if (childResult) return childResult
  }
  return null
}

const HierarchicalCategoricalFilter = ({
  label,
  allNodes,
  displayNodes,
  selected,
  setSelected,
  shouldNodeBeExpanded,
}: Props) => {
  // the isInternal boolean determines if we're converting outside state for use
  // internally in this component (true), or if we're preparing to send it back (false)
  const processNodes = useCallback(
    (nodes: KeySet, isInternal = false) => {
      const selectedNodes = new Set(nodes)

      const testNode = (key: string, children: string[]) => {
        return isInternal
          ? selectedNodes.has(key)
          : hasEveryChild(children, selectedNodes)
      }

      const toggleNode = (value: string, shouldAdd: boolean) => {
        if (shouldAdd) {
          selectedNodes.add(value)
        } else {
          selectedNodes.delete(value)
        }
      }

      const processNode = (node: InternalNode) => {
        const nodeChildren = getChildrenKeys(node.children)
        // displayNodes excludes zero-count nodes, so it is NOT index-aligned
        // with allNodes — the matching allNodes entry must be found by key
        const allNode = allNodes.find(({ key }) => key === node.key)
        const allNodeChildren = allNode
          ? getChildrenKeys(allNode.children)
          : nodeChildren

        const isParentOnly =
          nodeChildren.length > 0 &&
          nodeChildren.every((child) => child.includes('/'))

        if (isParentOnly) {
          // isParentOnly checks nodeChildren.length > 0, so first element exists
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const parentKey = getCountryKey(nodeChildren[0]!)
          const isAllSameParent = nodeChildren.every((key) =>
            key.includes(parentKey)
          )

          if (testNode(parentKey, nodeChildren)) {
            if (isAllSameParent) {
              // Check if parentKey is actually different from children
              // (prevents bug where /FR is both parent and child)
              const hasDistinctParent = nodeChildren.some(
                (child) => child !== parentKey
              )

              if (hasDistinctParent) {
                toggleNode(parentKey, !isInternal)
                for (const nodeChild of nodeChildren) {
                  toggleNode(nodeChild, isInternal)
                }
              } else if (!isInternal) {
                // Single child where parent === child (e.g., only /FR displayable)
                // Expand to include all children from allNodes
                for (const nodeChild of allNodeChildren) {
                  toggleNode(nodeChild, true)
                }
              }
              // When isInternal=true and no distinct parent, keep children as-is
            } else if (!isInternal) {
              for (const nodeChild of allNodeChildren) {
                toggleNode(nodeChild, true)
              }
            }
          }
        } else {
          const nodeGrandchildren = getGrandchildrenKeys(node.children)

          if (nodeGrandchildren.length > 0) {
            // nodeGrandchildren.length > 0 ensures first element exists
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const grandparentKey = getCountryKey(nodeGrandchildren[0]!)

            if (testNode(grandparentKey, nodeGrandchildren)) {
              toggleNode(grandparentKey, !isInternal)

              for (const nodeChild of nodeGrandchildren) {
                toggleNode(nodeChild, isInternal)
              }

              if (isInternal) {
                for (const nodeChild of nodeChildren) {
                  toggleNode(nodeChild, false)
                }
              }
            } else if (!isInternal) {
              const directChildrenKeys = getChildKeys(node.children)
              const allDirectChildrenKeys = getChildKeys(
                allNode ? allNode.children : node.children
              )

              Object.entries(directChildrenKeys).forEach(([key]) => {
                const directChildArray = directChildrenKeys[key]
                const allDirectChildArray = allDirectChildrenKeys[key]
                // Both keys derived from same structure, both must exist
                if (
                  directChildArray &&
                  allDirectChildArray &&
                  hasEveryChild(directChildArray, selectedNodes)
                ) {
                  for (const childKey of allDirectChildArray) {
                    toggleNode(childKey, true)
                  }
                }
              })
            }
          }
        }
      }

      displayNodes.forEach(processNode)

      if (isInternal) {
        const flatDisplayNodes = new Set(
          displayNodes.flatMap((node) =>
            node.children.flatMap((child) =>
              child.children.length === 0
                ? child.key
                : getChildrenKeys(child.children)
            )
          )
        )

        // removeUnusedKeys
        return new Set(
          [...selectedNodes].filter((value) => flatDisplayNodes.has(value))
        )
      }

      return selectedNodes
    },
    [allNodes, displayNodes]
  )

  const getSelectedNodes = useMemo(
    () => processNodes(selected, true),
    [processNodes, selected]
  )

  const internalNodeStates: InternalNodeStateMap = useMemo(() => {
    const internalNodeStates: InternalNodeStateMap = {}

    const countChildren = (node: InternalNode) => {
      return node.children.reduce<[number, number]>(
        (acc, child) => {
          const selectedCount = getSelectedNodes.has(child.key) ? 1 : 0

          acc[0] += selectedCount + countChildren(child)[0]
          acc[1] += child.children.length > 0 ? countChildren(child)[1] : 1

          return acc
        },
        [0, 0]
      )
    }

    const computeState = (node: InternalNode) => {
      const [selectedChildrenCount, totalChildrenCount] = countChildren(node)

      if (selectedChildrenCount === 0) {
        internalNodeStates[node.key] = 'EMPTY'
      } else if (selectedChildrenCount === totalChildrenCount) {
        internalNodeStates[node.key] = 'FULL'
      } else {
        internalNodeStates[node.key] = 'PARTIAL'
      }

      node.children.forEach(computeState)
    }

    displayNodes.forEach(computeState)

    return internalNodeStates
  }, [displayNodes, getSelectedNodes])

  const toggleSelected = useCallback(
    (key: string, setOnly?: boolean) => {
      const clone = new Set(getSelectedNodes)

      if (setOnly) {
        clone.clear()
      }

      const iNode = findNodeByKey(displayNodes, key)

      if (iNode) {
        const toggleDescendents = (node: InternalNode) => {
          node.children.forEach(toggleDescendents)
          if (clone.has(node.key) && !setOnly) {
            clone.delete(node.key)
          } else {
            clone.add(node.key)
          }
        }
        toggleDescendents(iNode)
      } else if (clone.has(key) && !setOnly) {
        clone.delete(key)
      } else {
        clone.add(key)
      }

      setSelected(processNodes(clone))
    },
    [getSelectedNodes, displayNodes, setSelected, processNodes]
  )

  const toggleInternal = useCallback(
    (key: string, setOnly?: boolean) => {
      const clone = new Set(getSelectedNodes)

      if (setOnly) {
        clone.clear()
      }

      const iNode = findNodeByKey(displayNodes, key)

      if (!iNode) return

      // For nodes with no displayable children, use allNodes instead
      const allNode = findNodeByKey(allNodes, key)

      const addDescendents = (node: InternalNode) => {
        if (!node.children.length) {
          clone.add(node.key)
        }
        node.children.forEach(addDescendents)
      }

      const removeDescendents = (node: InternalNode) => {
        clone.delete(node.key)
        node.children.forEach(removeDescendents)
      }

      if (internalNodeStates[iNode.key] !== 'FULL' || setOnly) {
        // If displayNode has no children but allNode does, use allNode's children
        if (
          iNode.children.length === 0 &&
          allNode &&
          allNode.children.length > 0
        ) {
          addDescendents(allNode)
        } else {
          addDescendents(iNode)
        }
      } else if (allNode) {
        // Use allNode for removal to ensure all children are removed
        removeDescendents(allNode)
      } else {
        removeDescendents(iNode)
      }

      setSelected(processNodes(clone))
    },
    [
      allNodes,
      getSelectedNodes,
      displayNodes,
      internalNodeStates,
      setSelected,
      processNodes,
    ]
  )

  const trackFilterUsage = useTrackFilterUsage<{
    device: string
    filter_title: string
    filter_name: string
    is_selected: boolean
  }>('filters')
  const trackFilterClick = useCallback<TrackFilterClick>(
    ({ device, filter_name, is_selected }) => {
      trackFilterUsage({
        device,
        filter_title: label ?? '',
        filter_name,
        is_selected,
      })
    },
    [label, trackFilterUsage]
  )

  const nodeRangeItems = useMemo(() => {
    const items: Array<{ key: string; leafKeys: string[] }> = []
    const collectLeaves = (node: InternalNode): string[] => {
      if (node.children.length === 0) return [node.key]
      return node.children.flatMap(collectLeaves)
    }
    const flatten = (node: InternalNode) => {
      items.push({ key: node.key, leafKeys: collectLeaves(node) })
      node.children.forEach(flatten)
    }
    displayNodes.forEach(flatten)
    return items
  }, [displayNodes])

  const handleShiftRange = useCallback(
    (anchorKey: string, clickedKey: string) => {
      const fromIdx = nodeRangeItems.findIndex((i) => i.key === anchorKey)
      const toIdx = nodeRangeItems.findIndex((i) => i.key === clickedKey)
      if (fromIdx === -1 || toIdx === -1) return

      const clickedLeaves = nodeRangeItems[toIdx]?.leafKeys ?? []
      const targetState = !clickedLeaves.every((k) => getSelectedNodes.has(k))
      const minIdx = Math.min(fromIdx, toIdx)
      const maxIdx = Math.max(fromIdx, toIdx)

      const clone = new Set(getSelectedNodes)
      nodeRangeItems.slice(minIdx, maxIdx + 1).forEach(({ leafKeys }) => {
        leafKeys.forEach((key) => {
          if (targetState) {
            clone.add(key)
          } else {
            clone.delete(key)
          }
        })
      })
      setSelected(processNodes(clone))
    },
    [nodeRangeItems, getSelectedNodes, setSelected, processNodes]
  )

  const lastClickedKeyRef = useRef<string | null>(null)
  const suppressNextChangeRef = useRef(false)

  const handleNodeClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, key: string) => {
      if (e.shiftKey && lastClickedKeyRef.current !== null) {
        suppressNextChangeRef.current = true
        handleShiftRange(lastClickedKeyRef.current, key)
      }
      lastClickedKeyRef.current = key
    },
    [handleShiftRange]
  )

  return (
    <NodeList>
      {displayNodes.map((iNode) => (
        <InternalNodeComponent
          key={iNode.key}
          label={label}
          state={internalNodeStates[iNode.key] || 'EMPTY'}
          node={iNode}
          nodeStates={internalNodeStates}
          selected={getSelectedNodes}
          toggleSelected={toggleSelected}
          toggleInternal={toggleInternal}
          initialECState={
            shouldNodeBeExpanded ? shouldNodeBeExpanded(iNode) : false
          }
          trackFilterClick={trackFilterClick}
          onNodeClick={handleNodeClick}
          suppressNextChangeRef={suppressNextChangeRef}
        />
      ))}
    </NodeList>
  )
}

export default HierarchicalCategoricalFilter

export type { InternalNode }
